/**
 * Portal inbox bell (ADR-0020 P3) — the apps-plane notification surface.
 *
 * Lives in the `PortalShell` header next to the account menu. Polls the unread
 * count (cheap) and lazily lists the inbox when opened; clicking a notification
 * marks it read (and navigates if it carries a deep `link`). Apps-plane only —
 * recipient is the signed-in `app_user`; nothing to do with the operator bell.
 *
 * Mirrors the `AccountMenu` dropdown shape (own open state + click-away layer +
 * branded `portal-pop` panel). Disabled in the builder preview (`embedded`) so
 * it never polls a fake app.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { cn } from "@/lib/cn";
import { portalApi, type PortalNotification } from "@/lib/api-portal";

const POLL_MS = 30_000;

function severityDot(severity: string): string {
  if (severity === "warning") return "bg-warning";
  if (severity === "error") return "bg-danger";
  if (severity === "success") return "bg-success";
  return "bg-info";
}

function NotificationRow({
  n,
  onActivate,
}: {
  n: PortalNotification;
  onActivate: (n: PortalNotification) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onActivate(n)}
      className={cn(
        "flex w-full gap-2.5 border-b border-border px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted focus-visible:bg-muted focus-visible:outline-none",
        !n.read && "bg-foreground/[0.03]",
      )}
    >
      <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", n.read ? "bg-transparent" : severityDot(n.severity))} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className={cn("truncate text-sm", n.read ? "font-normal" : "font-semibold")}>{n.title}</span>
          {n.occurrence_count > 1 && (
            <span className="shrink-0 rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground">
              ×{n.occurrence_count}
            </span>
          )}
        </span>
        {n.body && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{n.body}</span>}
      </span>
    </button>
  );
}

export function NotificationBell({ appSlug, embedded }: { appSlug: string; embedded?: boolean }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const enabled = !embedded && !!appSlug;

  const countQ = useQuery({
    queryKey: ["portal-notif-count", appSlug],
    queryFn: () => portalApi.notificationsUnreadCount(appSlug),
    enabled,
    refetchInterval: POLL_MS,
  });
  const unread = countQ.data?.unread ?? 0;

  const listQ = useQuery({
    queryKey: ["portal-notif-list", appSlug],
    queryFn: () => portalApi.listNotifications(appSlug, { limit: 20 }),
    enabled: enabled && open,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["portal-notif-count", appSlug] });
    qc.invalidateQueries({ queryKey: ["portal-notif-list", appSlug] });
  };
  const markRead = useMutation({
    mutationFn: (id: string) => portalApi.markNotificationRead(appSlug, id),
    onSuccess: invalidate,
  });
  const markAll = useMutation({
    mutationFn: () => portalApi.markAllNotificationsRead(appSlug),
    onSuccess: invalidate,
  });

  if (!enabled) return null;

  const activate = (n: PortalNotification) => {
    if (!n.read) markRead.mutate(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const notifications = listQ.data?.notifications ?? [];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Notifiche"
        className="relative flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10 focus-visible:outline-none"
      >
        <Bell className="h-[18px] w-[18px]" strokeWidth={1.5} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-danger-foreground">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="portal-pop absolute right-0 top-full z-50 mt-1.5 w-80 overflow-hidden rounded-[var(--portal-radius)] border border-border bg-surface text-foreground shadow-lg"
          >
            <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
              <span className="text-sm font-semibold">Notifiche</span>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => markAll.mutate()}
                  disabled={markAll.isPending}
                  className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  <CheckCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Segna tutte lette
                </button>
              )}
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {listQ.isLoading ? (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">Caricamento…</p>
              ) : notifications.length === 0 ? (
                <p className="px-3 py-8 text-center text-sm text-muted-foreground">Nessuna notifica.</p>
              ) : (
                notifications.map((n) => <NotificationRow key={n.id} n={n} onActivate={activate} />)
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
